import React, { useState, useEffect } from 'react';
import { Bell, Save, Loader2, Eye, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import RichTextEditor from '../../components/ui/rich-text-editor';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoginPopup = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewType, setPreviewType] = useState(null);
  
  const [clientPopup, setClientPopup] = useState({
    enabled: false,
    title: '',
    subtitle: '',
    content: '',
    button_text: 'Got it!'
  });
  
  const [partnerPopup, setPartnerPopup] = useState({
    enabled: false,
    title: '',
    subtitle: '',
    content: '',
    button_text: 'Got it!'
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/login-popup`, {
        headers: getAuthHeader()
      });
      
      if (response.data.client_popup) {
        setClientPopup(prev => ({ ...prev, ...response.data.client_popup }));
      }
      if (response.data.partner_popup) {
        setPartnerPopup(prev => ({ ...prev, ...response.data.partner_popup }));
      }
    } catch (error) {
      console.error('Failed to fetch login popup settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/login-popup`,
        {
          client_popup: clientPopup,
          partner_popup: partnerPopup
        },
        { headers: getAuthHeader() }
      );
      toast.success('Login popup settings saved!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleClientChange = (field, value) => {
    setClientPopup(prev => ({ ...prev, [field]: value }));
  };

  const handlePartnerChange = (field, value) => {
    setPartnerPopup(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const previewData = previewType === 'client' ? clientPopup : partnerPopup;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-amber-500/10">
            <Bell className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Login Popup</h1>
            <p className="text-slate-400 text-sm">Configure popups shown after login</p>
          </div>
        </div>
        
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Info */}
      <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-4">
        <p className="text-slate-400 text-sm">
          These popups appear once when clients or partners log in. Users can check "Don't show this again" to dismiss. 
          <span className="text-amber-400"> When you update the content, the popup will show again to all users.</span>
        </p>
      </div>

      {/* Forms */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Client Popup Form */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-white">Client Popup</h3>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-400">
                {clientPopup.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <Switch
                checked={clientPopup.enabled}
                onCheckedChange={(checked) => handleClientChange('enabled', checked)}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Title</label>
              <Input
                value={clientPopup.title}
                onChange={(e) => handleClientChange('title', e.target.value)}
                placeholder="e.g., What's New"
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Subtitle</label>
              <Input
                value={clientPopup.subtitle}
                onChange={(e) => handleClientChange('subtitle', e.target.value)}
                placeholder="e.g., Introducing Life Vault"
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Content</label>
              <RichTextEditor
                value={clientPopup.content}
                onChange={(value) => handleClientChange('content', value)}
                placeholder="Enter the main message content..."
              />
              <p className="text-xs text-slate-500 mt-1">Use the toolbar to format text with bold, italic, headings, lists, and more</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Button Text</label>
              <Input
                value={clientPopup.button_text}
                onChange={(e) => handleClientChange('button_text', e.target.value)}
                placeholder="e.g., Got it!"
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>

            <Button
              variant="outline"
              onClick={() => setPreviewType('client')}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
          </div>
        </div>

        {/* Partner Popup Form */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-white">Partner Popup</h3>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-400">
                {partnerPopup.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <Switch
                checked={partnerPopup.enabled}
                onCheckedChange={(checked) => handlePartnerChange('enabled', checked)}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Title</label>
              <Input
                value={partnerPopup.title}
                onChange={(e) => handlePartnerChange('title', e.target.value)}
                placeholder="e.g., What's New"
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Subtitle</label>
              <Input
                value={partnerPopup.subtitle}
                onChange={(e) => handlePartnerChange('subtitle', e.target.value)}
                placeholder="e.g., Introducing Life Vault"
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Content</label>
              <RichTextEditor
                value={partnerPopup.content}
                onChange={(value) => handlePartnerChange('content', value)}
                placeholder="Enter the main message content..."
              />
              <p className="text-xs text-slate-500 mt-1">Use the toolbar to format text with bold, italic, headings, lists, and more</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Button Text</label>
              <Input
                value={partnerPopup.button_text}
                onChange={(e) => handlePartnerChange('button_text', e.target.value)}
                placeholder="e.g., Got it!"
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>

            <Button
              variant="outline"
              onClick={() => setPreviewType('partner')}
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {previewType && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden relative">
            {/* Header accent */}
            <div className="h-1 bg-gradient-to-r from-amber-500 to-amber-600" />
            
            {/* Close button */}
            <button
              onClick={() => setPreviewType(null)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors z-10"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="p-8 text-center">
              {/* Title */}
              {previewData.title && (
                <p className="text-amber-400 text-sm font-medium tracking-wide uppercase mb-2">
                  {previewData.title}
                </p>
              )}
              
              {/* Subtitle */}
              {previewData.subtitle && (
                <h2 className="text-2xl font-bold text-white mb-4">
                  {previewData.subtitle}
                </h2>
              )}
              
              {/* Content */}
              {previewData.content && (
                <div 
                  className="text-slate-400 text-sm leading-relaxed mb-6 text-left
                    [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-2 [&_h1]:text-white
                    [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_h2]:text-white
                    [&_p]:mb-2 [&_p]:text-slate-300
                    [&_ul]:list-disc [&_ul]:ml-4 [&_ul]:mb-2
                    [&_ol]:list-decimal [&_ol]:ml-4 [&_ol]:mb-2
                    [&_li]:text-slate-300 [&_li]:mb-1
                    [&_b]:font-bold [&_strong]:font-bold
                    [&_i]:italic [&_em]:italic
                    [&_u]:underline"
                  dangerouslySetInnerHTML={{ __html: previewData.content }}
                />
              )}
              
              {/* Button */}
              <Button
                onClick={() => setPreviewType(null)}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-8"
              >
                {previewData.button_text || 'Got it!'}
              </Button>
              
              {/* Don't show again checkbox preview */}
              <div className="mt-4 flex items-center justify-center gap-2">
                <input type="checkbox" className="rounded border-slate-600" disabled />
                <span className="text-slate-500 text-xs">Don't show this again</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLoginPopup;
